import {
	createContext,
	useContext,
	useState,
	useRef,
	cloneElement,
	isValidElement,
	useMemo,
} from "react";
import {
	useFloating,
	offset,
	flip,
	shift,
	autoUpdate,
	useDismiss,
	useRole,
	useClick,
	useInteractions,
	FloatingPortal,
	FloatingFocusManager,
	arrow as arrowMw,
	FloatingArrow,
} from "@floating-ui/react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./FilterSortPopover.scss";

const FilterSortPopoverContext = createContext(null);

const useFilterSortPopoverContext = () => {
	const ctx = useContext(FilterSortPopoverContext);
	if (!ctx) {
		throw new Error(
			"FilterSortPopover components must be used inside <FilterSortPopover.Root>."
		);
	}
	return ctx;
};

const FilterSortPopoverRoot = ({
	id,
	children,
	open: controlledOpen,
	defaultOpen = false,
	onOpenChange,
	onOpen,
	onClose,
	onToggle,
	placement = "bottom",
	lockPlacement = true,
	strategy = "fixed",
	modal = false,
	offsetValue = 10,
	arrowPadding = 8,
	shiftPadding = 8,
}) => {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);

	const isControlled = typeof controlledOpen === "boolean";
	const open = isControlled ? controlledOpen : uncontrolledOpen;
	const setOpenState = onOpenChange || setUncontrolledOpen;

	const arrowRef = useRef(null);

	const middleware = [
		offset(offsetValue),
		...(!lockPlacement ? [flip()] : []),
		shift({ padding: shiftPadding }),
		arrowMw({ element: arrowRef, padding: arrowPadding }),
	];

	const { refs, floatingStyles, context, placement: currentPlacement } = useFloating({
		open,
		onOpenChange: nextOpen => {
			setOpenState(nextOpen);

			if (nextOpen) {
				if (onOpen) {
					onOpen({ id, open: nextOpen });
				}
			} else if (onClose) {
				onClose({ id, open: nextOpen });
			}

			if (onToggle) {
				onToggle({ id, open: nextOpen });
			}
		},
		placement,
		strategy,
		whileElementsMounted: autoUpdate,
		middleware,
	});

	const dismiss = useDismiss(context, { outsidePressEvent: "mousedown" });
	const role = useRole(context, { role: "dialog" });
	const click = useClick(context, { event: "click" });

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	const value = useMemo(
		() => ({
			open,
			refs,
			floatingStyles,
			context,
			getReferenceProps,
			getFloatingProps,
			id,
			arrowRef,
			currentPlacement,
			modal,
		}),
		[
			open,
			refs,
			floatingStyles,
			context,
			getReferenceProps,
			getFloatingProps,
			id,
			arrowRef,
			currentPlacement,
			modal,
		]
	);

	return (
		<FilterSortPopoverContext.Provider value={value}>
			{children}
		</FilterSortPopoverContext.Provider>
	);
};

FilterSortPopoverRoot.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node.isRequired,
	open: PropTypes.bool,
	defaultOpen: PropTypes.bool,
	onOpenChange: PropTypes.func,
	onOpen: PropTypes.func,
	onClose: PropTypes.func,
	onToggle: PropTypes.func,
	placement: PropTypes.string,
	lockPlacement: PropTypes.bool,
	strategy: PropTypes.oneOf(["fixed", "absolute"]),
	modal: PropTypes.bool,
	offsetValue: PropTypes.number,
	arrowPadding: PropTypes.number,
	shiftPadding: PropTypes.number,
};

const FilterSortPopoverTrigger = ({ children }) => {
	const { refs, getReferenceProps, open } = useFilterSortPopoverContext();

	if (!isValidElement(children)) {
		return null;
	}

	const originalRef = children.ref;

	return cloneElement(
		children,
		getReferenceProps({
			...children.props,
			ref: node => {
				refs.setReference(node);
				if (typeof originalRef === "function") {
					originalRef(node);
				} else if (originalRef && typeof originalRef === "object") {
					originalRef.current = node;
				}
			},
			"aria-expanded": open,
			"aria-haspopup": "dialog",
		})
	);
};

FilterSortPopoverTrigger.propTypes = {
	children: PropTypes.element.isRequired,
};

const FilterSortPopoverContent = ({ children, className, arrowClassName, ...restProps }) => {
	const {
		open,
		refs,
		floatingStyles,
		getFloatingProps,
		context,
		arrowRef,
		currentPlacement,
		modal,
	} = useFilterSortPopoverContext();

	if (!open) {
		return null;
	}

	return (
		<FloatingPortal>
			<FloatingFocusManager context={context} modal={modal}>
				<div
					ref={refs.setFloating}
					style={floatingStyles}
					{...getFloatingProps({
						className: classNames("filter-sort-popover__container", className),
						"data-placement": currentPlacement,
						...restProps,
					})}
				>
					<div className="filter-sort-popover">
						<FloatingArrow
							ref={arrowRef}
							context={context}
							className={classNames("filter-sort-popover__arrow", arrowClassName)}
							width={12}
							height={8}
							fill="white"
							stroke="white"
							strokeWidth={1}
						/>
						{children}
					</div>
				</div>
			</FloatingFocusManager>
		</FloatingPortal>
	);
};

FilterSortPopoverContent.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	arrowClassName: PropTypes.string,
};

export const FilterSortPopover = {
	Root: FilterSortPopoverRoot,
	Trigger: FilterSortPopoverTrigger,
	Content: FilterSortPopoverContent,
};
